import React from 'react';

export interface Specialist {
  name: string;
  role: string;
  photo: string;
  bio?: string;
  credentials?: string[];
  services?: string[];
}

interface Props {
  specialist: Specialist;
}

const SpecialistCard: React.FC<Props> = ({ specialist }) => {
  return (
    <div className="group relative bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/60 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col h-full">
      {/* Top image */}
      <div className="relative rounded-t-2xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-tr from-secondary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="aspect-[4/3] w-full bg-gray-100">
          <img
            src={specialist.photo}
            alt={`Foto de ${specialist.name}`}
            className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-[1.05]"
          />
        </div>
      </div>

      {/* Content */}
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-serif font-bold text-lg text-gray-800">{specialist.name}</h3>
            <p className="text-sm text-secondary font-semibold mt-1">{specialist.role}</p>
          </div>
          <div className="hidden sm:flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-secondary/15 to-primary/15 border border-secondary/30">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-secondary" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c-3.866 0-7 1.343-7 3v3h14v-3c0-1.657-3.134-3-7-3zm0-7a4 4 0 100 8 4 4 0 000-8z" />
            </svg>
          </div>
        </div>

        {/* Bio */}
        {specialist.bio && (
          <p className="mt-4 text-sm text-gray-600 leading-relaxed">
            {specialist.bio}
          </p>
        )}

        {/* Services list */}
        {specialist.services && specialist.services.length > 0 && (
          <div className="mt-4">
            <p className="text-xs text-gray-500 mb-2 font-semibold">Especialidades</p>
            <div className="flex flex-wrap gap-2">
              {specialist.services.map((srv) => (
                <span
                  key={srv}
                  className="inline-flex items-center px-3 py-1 text-xs rounded-full bg-white border border-gray-200 text-gray-700 hover:border-secondary/50 hover:text-secondary transition-colors"
                >
                  {srv}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Credentials */}
        {specialist.credentials && specialist.credentials.length > 0 && (
          <div className="mt-4">
            <p className="text-xs text-gray-500 mb-2 font-semibold">Formação e Credenciais</p>
            <ul className="space-y-1.5">
              {specialist.credentials.map((cred, idx) => (
                <li key={idx} className="flex items-start gap-2 text-xs text-gray-700">
                  <span className="text-secondary mt-1">•</span>
                  <span className="flex-1">{cred}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Bottom accent line */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-secondary/30 to-transparent" />
    </div>
  );
};

export default SpecialistCard;